<template>
    <div class="p-4">
        <h2>Station adjustment</h2>
        <div v-if="stationAdjustmentAllowed" class="d-flex flex-row">
            <div class="ds-text-regular mt-2 mb-2" style="width: 500px;">
                <h2 class>Actions</h2>
                <div>
                    <el-button
                        title="Export template xslx-file"
                        id="test-id-export-station-adjustment-template"
                        @click="exportStationAdjustmentTemplate">
                        EXPORT TEMPLATE FOR STATION ADJUSTMENT
                    </el-button>
                </div>
                <div class="d-flex flex-row align-center pt-4">
                    <FileInput
                        :labelText="'IMPORT STATION ADJUSTMENT'"
                        :customClasses="'el-button el-button--primary'"
                        :title="'Select xlsx-file'"
                        :onFileSelected="importStationAdjustmentXlsx"
                        :accept="'.xlsx'"
                        :idTag="'layout-xlsx-import-button'" />
                    <span class="text-small pl-4">{{importedXlsxName}}</span>
                </div>
                <div class="d-flex flex-row align-center pt-4">
                    <FileInput
                        :labelText="'IMPORT LAYOUT FILE'"
                        :customClasses="'el-button el-button--primary'"
                        :title="'Select layf-file'"
                        :onFileSelected="importLayoutLayf"
                        :accept="'.layf'"
                        :idTag="'layout-layf-import-button'" />
                    <span class="text-small pl-4">{{importedLayfName}}</span>
                </div>
                <div>
                    <el-button
                        :disabled="importedXlsxName === '' || importedLayfName === ''"
                        :title="importedXlsxName === '' || importedLayfName === '' ? 'Select file(s) first' : ''"
                        id="test-id-send-station-adjustment"
                        type="primary"
                        class="mt-5"
                        :loading="stationAdjustmentSent"
                        @click="sendStationAdjustmentData">
                        ADJUST STATIONS
                    </el-button>
                </div>
            </div>
            <div class="ds-text-regular mt-2 mb-2 pr-5 pl-5" style="width: 600px;">
                <h2>Instructions</h2>
                <p>
                    1) Click <i>EXPORT TEMPLATE FOR STATION ADJUSTMENT</i> button to download a xlsx-file with correct column headers. This is not mandatory, but it is recommended way to ensure that the column headers are correct.
                </p>
                <div>
                    2) Insert wanted adjustments into xlsx-file.
                    <ul>
                        <li>Insert Station ID.</li>
                        <li>Insert wanted depth adjustment (insert 0 or leave empty, if not needed). This will move the station and the point where the station is located. Negative adjustment will move the station deeper and positive adjustment will move the station forward.</li>
                        <li>Insert wanted sideways adjustment (insert 0 or leave empty, if not needed) and amount of in and out segments that are wanted to be moved (default value of 2 is used, if empty). Negative adjustment will move the station and segments to the right (relatively to the station's point orientation) and positive adjustment will move the station and segments to the left (relatively to the station's point orientation).</li>
                    </ul>
                    <img src="@/assets/instructions/bulk_adjust_stations_1.png"/><br/><br/>
                    <img src="@/assets/instructions/bulk_adjust_stations_2.png"/>
                </div>
                <p>
                    3) Save changes to xlsx.
                </p>
                <p>
                    4) Click <i>IMPORT STATION ADJUSTMENT</i>, select the xlsx and click <i>Open</i>.
                </p>
                <p>
                    5) Click <i>IMPORT LAYOUT FILE</i>, select the layf file you want to adjust and click <i>Open</i>.
                </p>
                <p>
                    6) Click <i>ADJUST STATIONS</i>.
                </p>
                <p>
                    7) When adjustment is done, zip package containing the modified layf file and change log will be downloaded automatically.
                </p>
            </div>
        </div>
        <div v-else>
            <p>You do not have access rights to bulk adjustment feature.</p>
        </div>
    </div>
</template>

<style lang="scss" scoped>
</style>

<script lang='ts'>
import FileInput from '@/components/FileInput.vue';
import { ImportStationAdjustmentXlsx, ImportLayoutLayf, SendStationAdjustmentData } from '@/store/bulk/actions';
import { ElMessage } from 'element-plus';
import { FileImportMessage } from '@/store/bulk/types';
import { downloadFile, base64ToBlob } from '@/util/utils';
import { trackEvent, trackError } from '@/util/telemetry';
import * as XLSX from 'xlsx';
import { Loading } from '@element-plus/icons-vue';

import { defineComponent } from 'vue';

export default defineComponent({
    name: 'BulkAdjustment',
    data() {
        return {
            stationAdjustmentAllowed: this.$store.state.user.stationAdjustmentAllowed,
            stationAdjustmentSent: false,
            downloadingLicenses: false,
        };
    },
    methods: {
        async importStationAdjustmentXlsx(file: File, fileName: string) {
            const res: FileImportMessage = await this.$store.dispatch(new ImportStationAdjustmentXlsx(file, fileName));
            ElMessage({
                message: res.msg,
                type: res.success ? 'success' : 'error',
                showClose: true,
                duration: 3000,
            });
            if (!res.success) trackError(res.msg, 'LP-ERR-FE-001', 'importStationAdjustmentXlsx');
        },
        async importLayoutLayf(file: File, fileName: string) {
            const res: FileImportMessage = await this.$store.dispatch(new ImportLayoutLayf(file, fileName));
            ElMessage({
                message: res.msg,
                type: res.success ? 'success' : 'error',
                showClose: true,
                duration: 3000,
            });
            if (!res.success) trackError(res.msg, 'LP-ERR-FE-001', 'importLayoutMdb');
        },
        async sendStationAdjustmentData() {
            trackEvent('AdjustStations');
            this.stationAdjustmentSent = true;
            const res: Response = await this.$store.dispatch(new SendStationAdjustmentData());
            if (res) {
                const responseClone = res.clone();
                try {
                    const resMsg = await res.json();
                    this.stationAdjustmentSent = false;
                    if (res.status === 200) {
                        const fileType = 'application/zip';
                        const fileName = 'bulk_adjustment.zip';
                        const blob = base64ToBlob(resMsg, fileType);
                        downloadFile(blob, fileType, fileName);
                    }
                    ElMessage({
                        message: res.status === 200 ? 'File downloaded' : resMsg,
                        type: res.status === 200 ? 'success' : 'error',
                        showClose: true,
                        duration: res.status === 200 ? 3000 : 0,
                    });
                    if (res.status !== 200) trackError(resMsg, 'LP-ERR-FE-002', 'sendStationAdjustmentData');
                } catch (error) {
                    this.stationAdjustmentSent = false;
                    const response = await responseClone.text();
                    ElMessage({
                        message: 'Unexpected error when adjusting stations ' + response,
                        type: 'error',
                        showClose: true,
                        duration: 0 });
                    trackError('Unexpected error when adjusting stations ' + response, 'LP-ERR-FE-002', 'sendStationAdjustmentData');
                }
            }
        },
        async exportStationAdjustmentTemplate() {
            const data = [['Station ID', 'Depth Offset', 'Sideways Offset', 'Sideways Segments In', 'Sideways Segments Out']];
            const columnWidths = [
                {wch: 12},
                {wch: 13},
                {wch: 17},
                {wch: 22},
                {wch: 23},
            ];
            const ws = XLSX.utils.aoa_to_sheet(data);
            ws['!cols'] = columnWidths;
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'StatusMapping');
            XLSX.write(wb, {bookType: 'xlsx', bookSST: true, type: 'base64'});
            XLSX.writeFile(wb, 'template.xlsx');
            ElMessage({
                message: 'Template exported',
                type: 'success',
                showClose: true,
                duration: 3000,
            });
        },
    },
    computed: {
        importedXlsxName(): string {
            return this.$store.state.bulk.stationAdjustmentData.stationAdjustmentXlsxName;
        },
        importedLayfName(): string {
            return this.$store.state.bulk.stationAdjustmentData.layoutLayfName;
        },
    },
    components: {
        FileInput,
        Loading,
    },
});
</script>
